import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { CalendarEventPopover } from "../../components/calendar/CalendarEventPopover";
import { eventsApi, schedulesApi } from "../../api";
import { workspaceNav } from "../../routes/workspaceNavigation";
import { useAuth } from "../../context/AuthContext";
import { useDeviceTier } from "../../hooks/useDeviceTier";
import { FilterShell } from "../../components/ui/FilterShell";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import type { EventListItem } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { resolveHoldLevel } from "../../utils/eventHoldLevel";
import { isPastDateKey, todayDateKey } from "../../utils/calendarDates";
import { findVenueConflict, isCancelledStatus, venueConflictMessage } from "../../utils/eventForm";
import "./CalendarPage.css";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const MAX_VISIBLE_CHIPS = 3;

/**
 * "agenda" is the phone default: a seven-column month grid gives ~48px cells
 * at 360px, too small to read a show name in or tap reliably. The grid stays
 * available as a secondary view for people who want the month shape.
 */
type ViewMode = "agenda" | "month" | "week" | "year";

const CALENDAR_POLL_MS = 30_000;
const CALENDAR_CLOCK_MS = 60_000;

function periodNavLabels(viewMode: ViewMode): { prev: string; next: string } {
  if (viewMode === "year") return { prev: "Previous year", next: "Next year" };
  if (viewMode === "week") return { prev: "Previous week", next: "Next week" };
  // "agenda" falls through to the month labels below: it pages by month.
  return { prev: "Previous month", next: "Next month" };
}

function NavChevron({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg className="calendar-page__nav-icon" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d={direction === "prev" ? "M12.5 15.5L7 10l5.5-5.5" : "M7.5 4.5L13 10l-5.5 5.5"}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Props = {
  workspace: WorkspaceId;
};

type CalendarDay = {
  date: Date;
  dateKey: string;
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isPast: boolean;
  isFutureOrToday: boolean;
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function eventDateKey(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : toDateKey(d);
}

function eventSortTime(event: EventListItem): number {
  if (event.start_date) {
    const t = new Date(event.start_date).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const d = new Date(event.event_date).getTime();
  return Number.isNaN(d) ? 0 : d;
}

function buildMonthGrid(viewDate: Date, today: Date): CalendarDay[] {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStart = startOfDay(today);

  return Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(year, month, i + 1);
    const dateStart = startOfDay(date);
    return {
      date,
      dateKey: toDateKey(date),
      inMonth: true,
      isToday: dateStart.getTime() === todayStart.getTime(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isPast: dateStart.getTime() < todayStart.getTime(),
      isFutureOrToday: dateStart.getTime() >= todayStart.getTime(),
    };
  });
}

/** Leading blank cells so day 1 lands on the correct weekday column. */
function monthStartPad(viewDate: Date): number {
  return new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
}

/** Trailing blanks to complete the final week row. */
function monthEndPad(startPad: number, dayCount: number): number {
  const rem = (startPad + dayCount) % 7;
  return rem === 0 ? 0 : 7 - rem;
}

function buildWeekDays(anchorKey: string, today: Date): CalendarDay[] {
  const anchor = parseDateKey(anchorKey);
  const weekStart = addDays(anchor, -anchor.getDay());
  const todayStart = startOfDay(today);

  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    const dateStart = startOfDay(date);
    return {
      date,
      dateKey: toDateKey(date),
      inMonth: true,
      isToday: dateStart.getTime() === todayStart.getTime(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isPast: dateStart.getTime() < todayStart.getTime(),
      isFutureOrToday: dateStart.getTime() >= todayStart.getTime(),
    };
  });
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatShortDate(iso: string): string {
  const d = parseDateKey(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

type DatePreset = "all" | "upcoming" | "this_month" | "past" | "custom";

const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: "all", label: "All dates" },
  { value: "upcoming", label: "Upcoming" },
  { value: "this_month", label: "This month" },
  { value: "past", label: "Past shows" },
  { value: "custom", label: "Custom range" },
];

const CALENDAR_STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Drafted" },
  { value: "cancelled", label: "Cancelled" },
] as const;

type CalendarStatusFilter = (typeof CALENDAR_STATUS_FILTERS)[number]["value"] | "all";

function normalizeStatus(status: string): string {
  return status.trim().toLowerCase().replace(/\s+/g, "_");
}

function matchesStatus(event: EventListItem, statusFilter: CalendarStatusFilter): boolean {
  if (statusFilter === "all") return true;
  const s = normalizeStatus(event.status);
  if (statusFilter === "draft") {
    return s === "draft" || s === "drafted" || s === "planned";
  }
  if (statusFilter === "active") {
    return s === "active" || s === "on_sale" || s === "live" || s === "confirmed";
  }
  if (statusFilter === "cancelled") {
    return s === "cancelled" || s === "canceled";
  }
  return s === statusFilter;
}

function matchesDatePreset(event: EventListItem, preset: DatePreset, today: Date, dateFrom: string, dateTo: string): boolean {
  const key = eventDateKey(event.event_date);
  const eventDay = startOfDay(parseDateKey(key));
  if (Number.isNaN(eventDay.getTime())) return false;
  const todayStart = startOfDay(today);

  if (preset === "upcoming") return eventDay.getTime() >= todayStart.getTime();
  if (preset === "past") return eventDay.getTime() < todayStart.getTime();
  if (preset === "this_month") {
    return eventDay.getFullYear() === today.getFullYear() && eventDay.getMonth() === today.getMonth();
  }
  if (preset === "custom") {
    if (dateFrom && key < dateFrom) return false;
    if (dateTo && key > dateTo) return false;
    return true;
  }
  return true;
}

function matchesCity(event: EventListItem, cityFilter: string): boolean {
  if (cityFilter === "all") return true;
  return (event.city ?? "").trim().toLowerCase() === cityFilter.trim().toLowerCase();
}

function matchesArtist(event: EventListItem, artistFilter: string): boolean {
  if (artistFilter === "all") return true;
  return (event.artist ?? "").trim().toLowerCase() === artistFilter.trim().toLowerCase();
}

function formatChipTime(event: EventListItem): string | null {
  if (!event.start_date) return null;
  const start = new Date(event.start_date);
  if (Number.isNaN(start.getTime())) return null;
  return start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (s === "active" || s === "on_sale") return "live";
  if (s === "completed") return "done";
  if (s === "cancelled") return "cancelled";
  if (s === "planned") return "planned";
  return "draft";
}

function matchesSearch(event: EventListItem, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    event.name.toLowerCase().includes(q) ||
    event.city.toLowerCase().includes(q) ||
    (event.artist?.toLowerCase().includes(q) ?? false) ||
    event.status.toLowerCase().includes(q)
  );
}

function densityClass(count: number): string {
  if (count >= 4) return "is-dense-4";
  if (count === 3) return "is-dense-3";
  if (count === 2) return "is-dense-2";
  if (count === 1) return "is-dense-1";
  return "";
}

const CalendarPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const inspectorRef = React.useRef<HTMLElement>(null);
  const [today, setToday] = useState(() => new Date());
  const todayKey = useMemo(() => todayDateKey(today), [today]);

  const tier = useDeviceTier();
  const isMobile = tier === "mobile";
  // Resolved once, from the width at mount, so a later resize never yanks the
  // view out from under a choice the user has made.
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "agenda" : "month"
  );
  /** Mobile only: the filters rail becomes a bottom sheet behind this flag. */
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Leaving the mobile tier mid-session would otherwise strand the sheet's
  // open state on a layout that renders the rail inline.
  useEffect(() => {
    if (!isMobile) setFiltersOpen(false);
  }, [isMobile]);
  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDateKey, setSelectedDateKey] = useState<string>(todayKey);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<CalendarStatusFilter>("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [artistFilter, setArtistFilter] = useState("all");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /** Drives the badge on the mobile Filters button, so a filtered view is
      obvious once the rail is hidden behind a sheet. */
  const activeFilterCount = useMemo(
    () =>
      [
        statusFilter !== "all",
        cityFilter !== "all",
        artistFilter !== "all",
        datePreset !== "all",
        Boolean(dateFrom),
        Boolean(dateTo),
        Boolean(searchQuery.trim()),
      ].filter(Boolean).length,
    [statusFilter, cityFilter, artistFilter, datePreset, dateFrom, dateTo, searchQuery]
  );
  const [dragEventId, setDragEventId] = useState<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const filterJumpKeyRef = React.useRef("");
  const [dayPopover, setDayPopover] = useState<{
    dateKey: string;
    events: EventListItem[];
    rect: DOMRect;
    pinned: boolean;
  } | null>(null);
  const hoverTimerRef = React.useRef<number | null>(null);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
    staleTime: 20_000,
    refetchInterval: CALENDAR_POLL_MS,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const tick = () => setToday(new Date());
    const id = window.setInterval(tick, CALENDAR_CLOCK_MS);
    return () => window.clearInterval(id);
  }, []);

  const allEvents = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);
  const errorMsg =
    eventsQuery.error instanceof Error ? eventsQuery.error.message : eventsQuery.error ? String(eventsQuery.error) : "";

  const cities = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEvents) {
      if (e.city?.trim()) set.add(e.city.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEvents]);

  const artists = useMemo(() => {
    const set = new Set<string>();
    for (const e of allEvents) {
      if (e.artist?.trim()) set.add(e.artist.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEvents]);

  const events = useMemo(() => {
    return allEvents.filter((event) => {
      if (!matchesSearch(event, searchQuery)) return false;
      if (!matchesStatus(event, statusFilter)) return false;
      if (!matchesCity(event, cityFilter)) return false;
      if (!matchesArtist(event, artistFilter)) return false;
      if (!matchesDatePreset(event, datePreset, today, dateFrom, dateTo)) return false;
      return true;
    });
  }, [allEvents, artistFilter, cityFilter, dateFrom, datePreset, dateTo, searchQuery, statusFilter, today]);

  const rescheduleMutation = useMutation({
    mutationFn: async ({ eventId, dateKey }: { eventId: string; dateKey: string }) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return eventsApi.update(eventId, { event_date: dateKey }, token);
    },
    onMutate: async ({ eventId, dateKey }) => {
      const listKey = queryKeys.events.list(workspace);
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<EventListItem[]>(listKey);
      if (previous) {
        queryClient.setQueryData<EventListItem[]>(
          listKey,
          previous.map((event) => (event.id === eventId ? { ...event, event_date: dateKey } : event)),
        );
      }
      return { previous };
    },
    onSuccess: async () => {
      toast.success("Event rescheduled");
    },
    onError: (err, _vars, context) => {
      const listKey = queryKeys.events.list(workspace);
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
      toast.error(err instanceof Error ? err.message : "Failed to reschedule event");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.events.list(workspace) });
    },
  });

  const eventsByDay = useMemo(() => {
    const map = new Map<string, EventListItem[]>();
    for (const event of events) {
      const key = eventDateKey(event.event_date);
      const bucket = map.get(key);
      if (bucket) bucket.push(event);
      else map.set(key, [event]);
    }
    for (const bucket of Array.from(map.values())) {
      bucket.sort((a, b) => eventSortTime(a) - eventSortTime(b) || a.name.localeCompare(b.name));
    }
    return map;
  }, [events]);

  const capacityByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const [key, dayEvents] of Array.from(eventsByDay.entries())) {
      map.set(key, dayEvents.reduce((sum, e) => sum + (e.venue_capacity || 0), 0));
    }
    return map;
  }, [eventsByDay]);

  const monthGrid = useMemo(() => buildMonthGrid(viewDate, today), [viewDate, today]);

  /** Only days with shows — an agenda listing empty dates is just a worse grid. */
  const agendaDays = useMemo(
    () =>
      monthGrid
        .filter((day) => day.inMonth && (eventsByDay.get(day.dateKey)?.length ?? 0) > 0)
        .map((day) => ({ day, dayEvents: eventsByDay.get(day.dateKey) ?? [] })),
    [monthGrid, eventsByDay]
  );
  const monthPadStart = useMemo(() => monthStartPad(viewDate), [viewDate]);
  const monthPadEnd = useMemo(
    () => monthEndPad(monthPadStart, monthGrid.length),
    [monthPadStart, monthGrid.length],
  );
  const weekDays = useMemo(() => buildWeekDays(selectedDateKey, today), [selectedDateKey, today]);
  const monthShowsTodayColumn = useMemo(() => monthGrid.some((day) => day.isToday), [monthGrid]);
  const todayColumnIndex = today.getDay();

  const periodSnapshot = useMemo(() => {
    if (viewMode !== "month" && viewMode !== "week") return null;
    const days = viewMode === "week" ? weekDays : monthGrid;
    let eventCount = 0;
    let busyDays = 0;
    let totalCapacity = 0;
    for (const day of days) {
      const count = (eventsByDay.get(day.dateKey) ?? []).length;
      if (count > 0) {
        busyDays += 1;
        eventCount += count;
        totalCapacity += capacityByDay.get(day.dateKey) ?? 0;
      }
    }
    return { eventCount, busyDays, dayCount: days.length, totalCapacity };
  }, [viewMode, weekDays, monthGrid, eventsByDay, capacityByDay]);

  const calendarEventIds = useMemo(() => {
    const ids = new Set<string>();
    const days = viewMode === "week" ? weekDays : monthGrid;
    for (const day of days) {
      for (const event of eventsByDay.get(day.dateKey) ?? []) ids.add(event.id);
    }
    return Array.from(ids).slice(0, 30);
  }, [viewMode, weekDays, monthGrid, eventsByDay]);

  const scheduleQueries = useQueries({
    queries: calendarEventIds.map((eventId) => ({
      queryKey: queryKeys.schedules(workspace, eventId),
      queryFn: () => schedulesApi.list(eventId, token!),
      enabled: Boolean(token) && calendarEventIds.length > 0,
      staleTime: 120_000,
    })),
  });

  const rosByEventId = useMemo(() => {
    const map = new Map<string, string>();
    calendarEventIds.forEach((eventId, index) => {
      const schedules = scheduleQueries[index]?.data ?? [];
      if (schedules.length === 0) return;
      const sorted = [...schedules].sort(
        (a, b) => new Date(a.scheduled_time).getTime() - new Date(b.scheduled_time).getTime(),
      );
      const time = new Date(sorted[0].scheduled_time);
      if (!Number.isNaN(time.getTime())) {
        map.set(eventId, time.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }));
      }
    });
    return map;
  }, [calendarEventIds, scheduleQueries]);

  const yearMonthDots = useMemo(() => {
    const year = viewDate.getFullYear();
    const counts = Array.from({ length: 12 }, () => 0);
    for (const e of events) {
      const d = new Date(e.event_date);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() === year) counts[d.getMonth()] += 1;
    }
    return counts;
  }, [events, viewDate]);

  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    statusFilter !== "all" ||
    cityFilter !== "all" ||
    artistFilter !== "all" ||
    datePreset !== "all" ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setStatusFilter("all");
    setCityFilter("all");
    setArtistFilter("all");
    setDatePreset("all");
    setDateFrom("");
    setDateTo("");
  }, []);

  const applyDatePreset = useCallback(
    (preset: DatePreset) => {
      setDatePreset(preset);
      if (preset !== "custom") {
        setDateFrom("");
        setDateTo("");
      }
      if (preset === "this_month") {
        const now = new Date();
        setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
        setSelectedDateKey(toDateKey(now));
      } else if (preset === "upcoming") {
        const now = new Date();
        setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
        setSelectedDateKey(toDateKey(now));
      }
    },
    [],
  );

  // When filters hide everything in the current month, jump to the nearest matching show.
  useEffect(() => {
    if (!hasActiveFilters) {
      filterJumpKeyRef.current = "";
      return;
    }
    if (events.length === 0) return;

    const jumpKey = [
      statusFilter,
      cityFilter,
      artistFilter,
      datePreset,
      dateFrom,
      dateTo,
      searchQuery.trim().toLowerCase(),
    ].join("|");

    const viewYear = viewDate.getFullYear();
    const viewMonth = viewDate.getMonth();
    const monthHasMatch = events.some((event) => {
      const d = parseDateKey(eventDateKey(event.event_date));
      return d.getFullYear() === viewYear && d.getMonth() === viewMonth;
    });
    if (monthHasMatch) {
      filterJumpKeyRef.current = jumpKey;
      return;
    }

    if (filterJumpKeyRef.current === jumpKey) return;
    filterJumpKeyRef.current = jumpKey;

    const sorted = [...events].sort(
      (a, b) => eventDateKey(a.event_date).localeCompare(eventDateKey(b.event_date)),
    );
    const todayKeyLocal = toDateKey(today);
    const nextUp =
      sorted.find((event) => eventDateKey(event.event_date) >= todayKeyLocal) ?? sorted[0];
    if (!nextUp) return;
    const d = parseDateKey(eventDateKey(nextUp.event_date));
    setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedDateKey(eventDateKey(nextUp.event_date));
  }, [
    artistFilter,
    cityFilter,
    dateFrom,
    datePreset,
    dateTo,
    events,
    hasActiveFilters,
    searchQuery,
    statusFilter,
    today,
    viewDate,
  ]);

  const handleDayDrop = (dateKey: string) => {
    if (!dragEventId || rescheduleMutation.isPending) return;
    if (isPastDateKey(dateKey, today)) {
      toast.error("Events cannot be moved to a past date.");
      setDragEventId(null);
      setDropTargetKey(null);
      return;
    }
    const event = allEvents.find((e) => e.id === dragEventId);
    if (!event) return;
    if (eventDateKey(event.event_date) === dateKey) {
      setDragEventId(null);
      setDropTargetKey(null);
      return;
    }
    // Moving to another day keeps the event's time of day, so shift its
    // timestamps by the same number of days for the overlap check.
    const dayDelta = Math.round(
      (parseDateKey(dateKey).getTime() - parseDateKey(eventDateKey(event.event_date)).getTime()) / 86_400_000,
    );
    const shift = (iso?: string | null) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      d.setDate(d.getDate() + dayDelta);
      return d.toISOString();
    };
    const conflict = findVenueConflict(allEvents, {
      venueId: event.venue_id ?? "",
      eventDate: dateKey,
      startAt: shift(event.start_date),
      endAt: shift(event.end_date),
      excludeEventId: event.id,
    });
    if (conflict && !isCancelledStatus(event.status)) {
      toast.error(venueConflictMessage(conflict));
      setDragEventId(null);
      setDropTargetKey(null);
      return;
    }
    rescheduleMutation.mutate({ eventId: dragEventId, dateKey }, { onSettled: () => {
      setDragEventId(null);
      setDropTargetKey(null);
    } });
  };

  const periodLabels = periodNavLabels(viewMode);

  const scrollInspectorIntoView = useCallback(() => {
    if (!window.matchMedia("(max-width: 1100px)").matches) return;
    inspectorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const goPrev = () => {
    if (viewMode === "month" || viewMode === "agenda") {
      setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    } else if (viewMode === "year") {
      setViewDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
    } else if (viewMode === "week") {
      setSelectedDateKey((prev) => toDateKey(addDays(parseDateKey(prev), -7)));
    }
  };

  const goNext = () => {
    if (viewMode === "month" || viewMode === "agenda") {
      setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    } else if (viewMode === "year") {
      setViewDate((prev) => new Date(prev.getFullYear() + 1, prev.getMonth(), 1));
    } else if (viewMode === "week") {
      setSelectedDateKey((prev) => toDateKey(addDays(parseDateKey(prev), 7)));
    }
  };

  const handleDaySelect = (day: CalendarDay, rect?: DOMRect) => {
    setSelectedDateKey(day.dateKey);
    if (viewMode === "month" && !day.inMonth) {
      setViewDate(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
    }
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    const dayEvents = eventsByDay.get(day.dateKey) ?? [];
    const anchor =
      rect ??
      new DOMRect(
        Math.max(12, window.innerWidth / 2 - 160),
        Math.max(80, window.innerHeight / 3),
        320,
        40,
      );
    setDayPopover({ dateKey: day.dateKey, events: dayEvents, rect: anchor, pinned: true });
    scrollInspectorIntoView();
  };

  const showDayHover = (day: CalendarDay, rect: DOMRect) => {
    const dayEvents = eventsByDay.get(day.dateKey) ?? [];
    if (dayEvents.length === 0) {
      if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
      setDayPopover((prev) => (prev?.pinned ? prev : null));
      return;
    }
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    setDayPopover((prev) => {
      if (prev?.pinned) return prev;
      return { dateKey: day.dateKey, events: dayEvents, rect, pinned: false };
    });
  };

  const clearDayHover = () => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      setDayPopover((prev) => (prev?.pinned ? prev : null));
    }, 60);
  };

  const closeDayPopover = useCallback(() => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    setDayPopover(null);
  }, []);

  // Drop popover when the month/view/filters change so it can't stick to a stale cell.
  useEffect(() => {
    setDayPopover(null);
  }, [viewDate, viewMode, statusFilter, cityFilter, artistFilter, datePreset, dateFrom, dateTo, searchQuery]);

  // Close pinned/hover card when leaving the calendar page.
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
      setDayPopover(null);
    };
  }, []);

  useEffect(() => {
    if (!dayPopover?.pinned) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDayPopover();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dayPopover?.pinned, closeDayPopover]);

  const jumpToMonth = (monthIndex: number) => {
    const target = new Date(viewDate.getFullYear(), monthIndex, 1);
    setViewDate(target);
    setViewMode("month");
    const isCurrentMonth = target.getFullYear() === today.getFullYear() && target.getMonth() === today.getMonth();
    setSelectedDateKey(toDateKey(isCurrentMonth ? today : target));
  };

  // Clicking any day inside a Year-view month opens that month (like
  // clicking its header) with the day selected, instead of a popup —
  // "open the month" is what people expect from a year overview.
  const handleYearDaySelect = (day: CalendarDay) => {
    setViewDate(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
    setViewMode("month");
    setSelectedDateKey(day.dateKey);
  };

  const moveSelection = useCallback((deltaDays: number) => {
    setSelectedDateKey((prev) => {
      const next = toDateKey(addDays(parseDateKey(prev), deltaDays));
      const nextDate = parseDateKey(next);
      if (viewMode === "month" || viewMode === "year") {
        setViewDate(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
      }
      return next;
    });
  }, [viewMode]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveSelection(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        moveSelection(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        moveSelection(-7);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        moveSelection(7);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [moveSelection]);

  const navTitle =
    viewMode === "year"
      ? String(viewDate.getFullYear())
      : viewMode === "week"
        ? (() => {
            const start = weekDays[0].date;
            const end = weekDays[6].date;
            if (start.getMonth() === end.getMonth()) {
              return `${start.toLocaleDateString(undefined, { month: "long" })} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
            }
            return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
          })()
        : formatMonthYear(viewDate);

  const renderPeriodSnapshot = () => {
    if (!periodSnapshot) return null;
    const { eventCount, busyDays, dayCount, totalCapacity } = periodSnapshot;
    const utilization = dayCount > 0 ? Math.round((busyDays / dayCount) * 100) : 0;
    return (
      <div className="calendar-page__period-snapshot" aria-label={viewMode === "week" ? "This week summary" : "This month summary"}>
        <div className="calendar-page__period-snapshot-item">
          <span className="calendar-page__period-snapshot-value">{eventCount}</span>
          <span className="calendar-page__period-snapshot-label">Events</span>
        </div>
        <div className="calendar-page__period-snapshot-item">
          <span className="calendar-page__period-snapshot-value">{busyDays}</span>
          <span className="calendar-page__period-snapshot-label">Active days</span>
        </div>
        {totalCapacity > 0 ? (
          <div className="calendar-page__period-snapshot-item">
            <span className="calendar-page__period-snapshot-value">{totalCapacity.toLocaleString("en-IN")}</span>
            <span className="calendar-page__period-snapshot-label">Seats</span>
          </div>
        ) : null}
        <div className="calendar-page__period-snapshot-item calendar-page__period-snapshot-item--muted">
          <span className="calendar-page__period-snapshot-value">{utilization}%</span>
          <span className="calendar-page__period-snapshot-label">Days booked</span>
        </div>
      </div>
    );
  };

  const renderEventChip = (event: EventListItem, compact = false) => {
    const chipTime = formatChipTime(event);
    const hold = resolveHoldLevel(event);
    const rosTime = rosByEventId.get(event.id);
    return (
      <button
        key={event.id}
        type="button"
        draggable
        className={`calendar-page__chip calendar-page__chip--${statusTone(event.status)}${compact ? " calendar-page__chip--compact" : ""}${dragEventId === event.id ? " is-dragging" : ""}`}
        title={`${event.name} · ${event.city}${chipTime ? ` · ${chipTime}` : ""} · ${event.status} · drag to reschedule`}
        onDragStart={(e) => {
          setDragEventId(event.id);
          e.dataTransfer.setData("text/plain", event.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => {
          setDragEventId(null);
          setDropTargetKey(null);
        }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(workspaceNav.dashboard(workspace, event.id));
        }}
      >
        <span className="calendar-page__chip-dot" aria-hidden />
        <span className="calendar-page__chip-body">
          {hold ? <span className="calendar-page__chip-hold">H{hold}</span> : null}
          {chipTime && !compact ? <span className="calendar-page__chip-time">{chipTime}</span> : null}
          {rosTime && !compact ? <span className="calendar-page__chip-ros">ROS {rosTime}</span> : null}
          <span className="calendar-page__chip-label">{event.name}</span>
          {!compact ? <span className="calendar-page__chip-city">{event.city}</span> : null}
        </span>
      </button>
    );
  };

  const renderDayCell = (day: CalendarDay, inWeekView = false) => {
    const dayEvents = eventsByDay.get(day.dateKey) ?? [];
    const visibleEvents = dayEvents.slice(0, inWeekView ? 8 : MAX_VISIBLE_CHIPS);
    const overflow = dayEvents.length - visibleEvents.length;
    const isSelected = day.dateKey === selectedDateKey;
    const dayCapacity = capacityByDay.get(day.dateKey) ?? 0;

    const isTodayColumn = !inWeekView && monthShowsTodayColumn && day.date.getDay() === todayColumnIndex;

    const cellClass = [
      "calendar-page__day",
      !day.inMonth && !inWeekView ? "is-outside" : "",
      day.isToday ? "is-today" : "",
      isTodayColumn ? "is-today-col" : "",
      day.isPast ? "is-past" : "",
      day.isWeekend ? "is-weekend" : "",
      isSelected ? "is-selected" : "",
      dayEvents.length > 0 ? "has-events" : "",
      dropTargetKey === day.dateKey && dragEventId ? (day.isPast ? "is-drop-invalid" : "is-drop-target") : "",
      densityClass(dayEvents.length),
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        key={`${inWeekView ? "w" : "m"}-${day.dateKey}`}
        role="gridcell"
        className={cellClass}
        aria-selected={isSelected}
        aria-label={`${day.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${dayEvents.length ? `, ${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}` : ""}`}
        onDragOver={(e) => {
          if (!dragEventId) return;
          e.preventDefault();
          setDropTargetKey(day.dateKey);
          e.dataTransfer.dropEffect = day.isPast ? "none" : "move";
        }}
        onDragLeave={() => {
          setDropTargetKey((key) => (key === day.dateKey ? null : key));
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (day.isPast) {
            toast.error("Events cannot be moved to a past date.");
            setDragEventId(null);
            setDropTargetKey(null);
            return;
          }
          handleDayDrop(day.dateKey);
        }}
        onClick={(e) => handleDaySelect(day, e.currentTarget.getBoundingClientRect())}
        onMouseEnter={(e) => {
          if (dayEvents.length === 0) return;
          showDayHover(day, e.currentTarget.getBoundingClientRect());
        }}
        onMouseLeave={(e) => {
          const related = e.relatedTarget;
          if (related instanceof Node && e.currentTarget.contains(related)) return;
          clearDayHover();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleDaySelect(day, e.currentTarget.getBoundingClientRect());
          }
        }}
        tabIndex={isSelected ? 0 : -1}
      >
        <div className="calendar-page__day-head">
          {dayCapacity > 0 ? (
            <span className="calendar-page__day-cap" title={`${dayCapacity.toLocaleString("en-IN")} seats`}>
              {dayCapacity >= 1000 ? `${Math.round(dayCapacity / 1000)}k` : dayCapacity}
            </span>
          ) : (
            <span className="calendar-page__day-cap calendar-page__day-cap--empty" />
          )}
          <div className="calendar-page__day-head-end">
            {dayEvents.length > 0 ? (
              <span
                className="calendar-page__day-events-badge"
                title={`${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}`}
              >
                {dayEvents.length}
              </span>
            ) : null}
            <span className="calendar-page__day-num">{day.date.getDate()}</span>
          </div>
        </div>

        <div className={`calendar-page__day-events${inWeekView ? " calendar-page__day-events--week" : ""}`}>
          {visibleEvents.map((event) => renderEventChip(event, !inWeekView))}
          {overflow > 0 ? (
            <span className="calendar-page__overflow">+{overflow} more</span>
          ) : null}
        </div>
      </div>
    );
  };

  const renderCalendarCanvas = () => {
    if (eventsQuery.isPending) {
      return (
        <div className="calendar-page__loading" aria-busy="true" aria-label="Loading calendar">
          <div className="calendar-page__loading-grid">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="calendar-page__loading-cell app-shell-skeleton-block" />
            ))}
          </div>
        </div>
      );
    }

    if (viewMode === "agenda") {
      return (
        <div className="calendar-agenda">
          {agendaDays.length === 0 ? (
            <p className="calendar-agenda__empty">
              No shows in {formatMonthYear(viewDate)}.
            </p>
          ) : (
            agendaDays.map(({ day, dayEvents }) => (
              <section key={day.dateKey} className="calendar-agenda__group">
                <header className={`calendar-agenda__date${day.isToday ? " is-today" : ""}`}>
                  <span className="calendar-agenda__dow">
                    {day.date.toLocaleDateString(undefined, { weekday: "short" })}
                  </span>
                  <span className="calendar-agenda__dom">{day.date.getDate()}</span>
                  {day.isToday ? <span className="calendar-agenda__today">Today</span> : null}
                </header>

                <div className="calendar-agenda__events">
                  {dayEvents.map((event) => {
                    const chipTime = formatChipTime(event);
                    const hold = resolveHoldLevel(event);
                    return (
                      <button
                        key={event.id}
                        type="button"
                        className={`calendar-agenda__event calendar-agenda__event--${statusTone(event.status)}`}
                        onClick={() => navigate(workspaceNav.dashboard(workspace, event.id))}
                      >
                        <span className="calendar-agenda__event-main">
                          <span className="calendar-agenda__event-name">{event.name}</span>
                          <span className="calendar-agenda__event-meta">
                            {chipTime ? <span>{chipTime}</span> : null}
                            {event.city ? <span>{event.city}</span> : null}
                            {hold ? <span>H{hold}</span> : null}
                          </span>
                        </span>
                        <span className="calendar-agenda__event-status">{event.status}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      );
    }

    if (viewMode === "month") {
      return (
        <>
          <div className="calendar-page__year-rail">
            <span className="calendar-page__year-rail-label">{viewDate.getFullYear()}</span>
            <div className="calendar-page__year-months">
              {MONTH_LABELS.map((label, i) => {
                const count = yearMonthDots[i];
                const isCurrent = i === viewDate.getMonth();
                return (
                  <button
                    key={label}
                    type="button"
                    className={`calendar-page__year-month${isCurrent ? " is-current" : ""}${count > 0 ? " has-events" : ""}`}
                    onClick={() => jumpToMonth(i)}
                    title={`${label} — ${count} event${count === 1 ? "" : "s"}`}
                    aria-current={isCurrent ? "true" : undefined}
                  >
                    <span className="calendar-page__year-month-label">{label}</span>
                    <span className={`calendar-page__year-month-dot${count >= 4 ? " is-hot" : count >= 2 ? " is-warm" : ""}`} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="calendar-page__grid-wrap">
            {renderPeriodSnapshot()}
            <div className="calendar-page__weekdays" aria-hidden>
              {WEEKDAY_LABELS.map((label, index) => (
                <div
                  key={label}
                  className={[
                    "calendar-page__weekday",
                    monthShowsTodayColumn && index === todayColumnIndex ? "is-today-col" : "",
                    index === 0 || index === 6 ? "is-weekend-col" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {label}
                </div>
              ))}
            </div>
            <div className="calendar-page__grid" role="grid" aria-label={formatMonthYear(viewDate)}>
              {Array.from({ length: monthPadStart }, (_, i) => (
                <div key={`pad-start-${i}`} className="calendar-page__day is-empty" aria-hidden />
              ))}
              {monthGrid.map((day) => renderDayCell(day))}
              {Array.from({ length: monthPadEnd }, (_, i) => (
                <div key={`pad-end-${i}`} className="calendar-page__day is-empty" aria-hidden />
              ))}
            </div>
            <div className="calendar-page__hint-bar">
              <span className="calendar-page__hint-pill">← → day</span>
              <span className="calendar-page__hint-pill">↑ ↓ week</span>
              <span className="calendar-page__hint-text">Click for details · drag events to reschedule</span>
            </div>
          </div>
        </>
      );
    }

    if (viewMode === "year") {
      return (
        <div className="calendar-page__year-wrap">
          <div className="calendar-page__year-grid" role="grid" aria-label={`Year ${viewDate.getFullYear()}`}>
            {Array.from({ length: 12 }, (_, monthIndex) => {
              const monthDate = new Date(viewDate.getFullYear(), monthIndex, 1);
              const miniGrid = buildMonthGrid(monthDate, today);
              const monthCount = yearMonthDots[monthIndex];
              const isCurrentMonth =
                today.getFullYear() === viewDate.getFullYear() && today.getMonth() === monthIndex;

              return (
                <section
                  key={monthIndex}
                  className={`calendar-page__year-month-card${isCurrentMonth ? " is-current-month" : ""}${monthCount > 0 ? " has-events" : ""}`}
                  aria-label={formatMonthYear(monthDate)}
                >
                  <button
                    type="button"
                    className="calendar-page__year-month-card-head"
                    onClick={() => jumpToMonth(monthIndex)}
                  >
                    <span className="calendar-page__year-month-card-title">
                      {monthDate.toLocaleDateString(undefined, { month: "long" })}
                    </span>
                    {monthCount > 0 ? (
                      <span className="calendar-page__year-month-card-count">
                        {monthCount} event{monthCount === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </button>
                  <div className="calendar-page__year-mini-weekdays" aria-hidden>
                    {WEEKDAY_LABELS.map((label) => (
                      <span key={label} className="calendar-page__year-mini-weekday">
                        {label.charAt(0)}
                      </span>
                    ))}
                  </div>
                  <div className="calendar-page__year-mini-grid">
                    {miniGrid.map((day) => {
                      const dayEvents = eventsByDay.get(day.dateKey) ?? [];
                      const isSelected = day.dateKey === selectedDateKey;
                      const miniClass = [
                        "calendar-page__year-mini-day",
                        !day.inMonth ? "is-outside" : "",
                        day.isToday ? "is-today" : "",
                        day.isWeekend ? "is-weekend" : "",
                        isSelected ? "is-selected" : "",
                        dayEvents.length > 0 ? "has-events" : "",
                        densityClass(dayEvents.length),
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <button
                          key={day.dateKey}
                          type="button"
                          className={miniClass}
                          aria-label={
                            dayEvents.length > 0
                              ? `${day.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}, ${dayEvents.length} events`
                              : day.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
                          }
                          aria-pressed={isSelected}
                          onClick={() => handleYearDaySelect(day)}
                        >
                          <span className="calendar-page__year-mini-num">{day.date.getDate()}</span>
                          {dayEvents.length > 0 ? <span className="calendar-page__year-mini-dot" aria-hidden /> : null}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
          <p className="eventos-subtle calendar-page__hint">Click a month name or any day to open it in Month view</p>
        </div>
      );
    }

    if (viewMode === "week") {
      return (
        <div className="calendar-page__week-wrap">
          {renderPeriodSnapshot()}
          <div className="calendar-page__week-head">
            {weekDays.map((day) => (
              <div key={day.dateKey} className={`calendar-page__week-col-head${day.isToday ? " is-today" : ""}`}>
                <span className="calendar-page__week-col-day">{WEEKDAY_LABELS[day.date.getDay()]}</span>
                <button
                  type="button"
                  className={`calendar-page__week-col-date${day.dateKey === selectedDateKey ? " is-selected" : ""}`}
                  onClick={(e) => handleDaySelect(day, e.currentTarget.getBoundingClientRect())}
                  aria-pressed={day.dateKey === selectedDateKey}
                  aria-label={day.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                >
                  {day.date.getDate()}
                </button>
              </div>
            ))}
          </div>
          <div className="calendar-page__week-grid" role="grid" aria-label="Week view">
            {weekDays.map((day) => renderDayCell(day, true))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="app-shell-dash calendar-page calendar-page--fit">
      <header className="calendar-page__header">
        <div className="calendar-page__header-copy">
          <h1 className="app-shell-page-title calendar-page__title">Event Calendar</h1>
        </div>
        <div className="calendar-page__header-search">
          <label className="calendar-page__search-wrap">
            <span className="visually-hidden">Search events</span>
            <span className="calendar-page__search-icon" aria-hidden>⌕</span>
            <input
              className="eventos-input calendar-page__search"
              type="search"
              placeholder="Search events…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
        </div>
      </header>

      {eventsQuery.isError ? (
        <QueryErrorBanner
          message={errorMsg || "Request failed"}
          onRetry={() => void eventsQuery.refetch()}
          retrying={eventsQuery.isFetching}
        />
      ) : null}

      <section className="eventos-card calendar-page__workspace" aria-label="Calendar workspace">
        <div className="calendar-page__body">
          <div className="calendar-page__main-col">
            <div className="calendar-page__command">
              <div className="calendar-page__period-nav" aria-label="Navigate calendar period">
                <button
                  type="button"
                  className="calendar-page__nav-btn"
                  onClick={goPrev}
                  aria-label={periodLabels.prev}
                >
                  <NavChevron direction="prev" />
                </button>
                <h2 className="calendar-page__period-title">{navTitle}</h2>
                <button
                  type="button"
                  className="calendar-page__nav-btn"
                  onClick={goNext}
                  aria-label={periodLabels.next}
                >
                  <NavChevron direction="next" />
                </button>
              </div>
              <div className="calendar-page__view-toggle" role="tablist" aria-label="Calendar view">
                {(
                  isMobile
                    ? ([
                        { id: "agenda", label: "Agenda" },
                        { id: "month", label: "Month" },
                      ] as const)
                    : ([
                        { id: "week", label: "Week" },
                        { id: "month", label: "Month" },
                        { id: "year", label: "Year" },
                      ] as const)
                ).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="tab"
                    aria-selected={viewMode === option.id}
                    className={`calendar-page__view-btn${viewMode === option.id ? " is-active" : ""}`}
                    onClick={() => setViewMode(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Beside the view switch, where the other view controls live —
                  stranded under the canvas it read as an empty section. */}
              {isMobile ? (
                <button
                  type="button"
                  className={`mobile-filter-trigger${activeFilterCount > 0 ? " has-filters" : ""}`}
                  onClick={() => setFiltersOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={filtersOpen}
                >
                  <span>Filters</span>
                  {activeFilterCount > 0 ? (
                    <span className="mobile-filter-trigger__count">{activeFilterCount}</span>
                  ) : null}
                </button>
              ) : null}
            </div>

            <div
              id="calendar-panel"
              className="calendar-page__canvas"
              role="tabpanel"
              aria-label={`${viewMode === "week" ? "Week" : viewMode === "year" ? "Year" : viewMode === "agenda" ? "Agenda" : "Month"} calendar`}
            >
              {renderCalendarCanvas()}
            </div>
          </div>

          <FilterShell
            isMobile={isMobile}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            inspectorRef={inspectorRef}
            className="calendar-page__inspector"
          >
            <section className="calendar-page__filters-rail">
              <div className="calendar-page__filters-rail-head">
                <h2 className="calendar-page__filters-rail-title">Filters</h2>
                <p className="calendar-page__filters-summary" aria-live="polite">
                  {eventsQuery.isPending
                    ? "Loading…"
                    : events.length === allEvents.length
                      ? `${events.length} show${events.length === 1 ? "" : "s"}`
                      : `${events.length} of ${allEvents.length} shows`}
                </p>
              </div>

              <fieldset id="calendar-filters" className="calendar-page__filters-panel">
                <legend className="visually-hidden">Filter events</legend>

                <label className="calendar-page__filter-field">
                  <span className="calendar-page__filter-label">When</span>
                  <MobileSelect
                    label="When"
                    className="eventos-select calendar-page__filter-select"
                    value={datePreset}
                    onChange={(v) => applyDatePreset(v as DatePreset)}
                    options={DATE_PRESET_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                  />
                </label>

                {datePreset === "custom" ? (
                  <div className="calendar-page__filter-range">
                    <label className="calendar-page__filter-field">
                      <span className="calendar-page__filter-label">From</span>
                      <input
                        className="eventos-input calendar-page__filter-select"
                        type="date"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </label>
                    <label className="calendar-page__filter-field">
                      <span className="calendar-page__filter-label">To</span>
                      <input
                        className="eventos-input calendar-page__filter-select"
                        type="date"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </label>
                  </div>
                ) : null}

                <label className="calendar-page__filter-field">
                  <span className="calendar-page__filter-label">Status</span>
                  <MobileSelect
                    label="Status"
                    className="eventos-select calendar-page__filter-select"
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v as CalendarStatusFilter)}
                    options={[
                      { value: "all", label: "All statuses" },
                      ...CALENDAR_STATUS_FILTERS.map((status) => ({ value: status.value, label: status.label })),
                    ]}
                  />
                </label>

                <div className="calendar-page__filter-field">
                  <span className="calendar-page__filter-label">City</span>
                  <MobileSelect
                    label="City"
                    className="eventos-select calendar-page__filter-select"
                    value={cityFilter}
                    onChange={setCityFilter}
                    disabled={cities.length === 0}
                    options={[
                      { value: "all", label: cities.length === 0 ? "No cities yet" : "All cities" },
                      ...cities.map((city) => ({ value: city, label: city })),
                    ]}
                  />
                </div>

                <div className="calendar-page__filter-field">
                  <span className="calendar-page__filter-label">Artist</span>
                  <MobileSelect
                    label="Artist"
                    className="eventos-select calendar-page__filter-select"
                    value={artistFilter}
                    onChange={setArtistFilter}
                    disabled={artists.length === 0}
                    options={[
                      { value: "all", label: artists.length === 0 ? "No artists yet" : "All artists" },
                      ...artists.map((artist) => ({ value: artist, label: artist })),
                    ]}
                  />
                </div>

                {hasActiveFilters ? (
                  <div className="calendar-page__filter-chips" aria-label="Active filters">
                    {datePreset !== "all" ? (
                      <span className="calendar-page__filter-chip">
                        {DATE_PRESET_OPTIONS.find((o) => o.value === datePreset)?.label}
                        {datePreset === "custom" && (dateFrom || dateTo)
                          ? `: ${dateFrom || "…"} → ${dateTo || "…"}`
                          : ""}
                      </span>
                    ) : null}
                    {statusFilter !== "all" ? (
                      <span className="calendar-page__filter-chip">
                        {CALENDAR_STATUS_FILTERS.find((s) => s.value === statusFilter)?.label ?? statusFilter}
                      </span>
                    ) : null}
                    {cityFilter !== "all" ? (
                      <span className="calendar-page__filter-chip">{cityFilter}</span>
                    ) : null}
                    {artistFilter !== "all" ? (
                      <span className="calendar-page__filter-chip">{artistFilter}</span>
                    ) : null}
                    {searchQuery.trim() ? (
                      <span className="calendar-page__filter-chip">“{searchQuery.trim()}”</span>
                    ) : null}
                  </div>
                ) : null}

                {!eventsQuery.isPending && hasActiveFilters && events.length === 0 ? (
                  <p className="calendar-page__filters-empty">No shows match these filters.</p>
                ) : null}

                <button
                  type="button"
                  className="eventos-nav-btn calendar-page__clear-filters"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear filters
                </button>
              </fieldset>
            </section>
          </FilterShell>
        </div>
      </section>

      {dayPopover ? (
        <CalendarEventPopover
          key={`${dayPopover.dateKey}-${dayPopover.pinned ? "pin" : "hover"}`}
          mode="day"
          dateKey={dayPopover.dateKey}
          dateLabel={formatShortDate(dayPopover.dateKey)}
          events={dayPopover.events}
          anchorRect={dayPopover.rect}
          pinned={dayPopover.pinned}
          onClose={closeDayPopover}
          onMouseEnter={() => {
            if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
          }}
          onMouseLeave={() => {
            if (!dayPopover.pinned) clearDayHover();
          }}
          onAddEvent={
            dayPopover.pinned
              ? () => {
                  closeDayPopover();
                  navigate(workspaceNav.newEvent(workspace, dayPopover.dateKey));
                }
              : undefined
          }
          addDisabled={isPastDateKey(dayPopover.dateKey, today)}
          onOpenEvent={(event) => {
            closeDayPopover();
            navigate(workspaceNav.dashboard(workspace, event.id));
          }}
        />
      ) : null}
    </div>
  );
};

export default CalendarPage;
